import { FieldSchema } from '../../models/FieldSchema.model';
import { FieldType } from '../../models/FieldType.model';
import { FieldValidator } from '../../models/FieldValidator.model';
import { UnifiedFieldRecordKey } from '../../models/UnifiedFieldRecord.model';
import { ValidationRules } from '../../models/ValidationRuleNames.model';
import { createDefaultValidators } from '../../utils/create-default-validators/createDefaultValidators';
import {
    createAdditionalInsuredEndorsementValidatorsMap,
    ItemFieldValidator,
} from '../additional-insured-endorsement/createAdditionalInsuredEndorsementValidatorsMap';
import { createRequiredValidator } from '../commons/required.validator';

import { createNonEmptyValidator } from './nonEmpty.validator';

// Extended record key type to include $itemFields for sub-field validators
export type ListValidatorsRecordKey = UnifiedFieldRecordKey | '$itemFields';

export interface ListValidatorsMap {
    $self?: FieldValidator[];
    $item?: FieldValidator[];
    // Sub-field validators for complex object item types (e.g., AdditionalInsuredEndorsement)
    $itemFields?: Record<string, ItemFieldValidator[]>;
}

export function createListValidatorsMap({
    itemType,
    label,
    nullable,
    required,
    nonEmpty,
    customMessages,
}: {
    itemType: FieldType;
    label?: string;
    nullable?: boolean;
    required?: boolean;
    nonEmpty?: boolean;
    customMessages?: Record<ValidationRules, string>;
}): ListValidatorsMap {
    // Currently doesn't support nested object/list fields be cautious they need to be handle as special type.
    const validators: ListValidatorsMap = {};
    const itemValidators = [];
    const selfValidators = [];
    if (required) {
        // for future expansion may need schema to get field name label
        selfValidators.push(createRequiredValidator(
            nullable, { fieldName: label, customMsg: customMessages?.required },
        ));
        itemValidators.push(createRequiredValidator(
            nullable, { fieldName: label, customMsg: customMessages?.required },
        ));
    }
    if (nonEmpty) {
        selfValidators.push(createNonEmptyValidator({ fieldName: label, customMsg: customMessages?.['list.nonEmpty'] }));
    }
    validators.$self = [ ...selfValidators ] as FieldValidator[];

    // Handle complex object item types with sub-field validators
    if (itemType === 'AdditionalInsuredEndorsement') {
        const subFieldValidators = createAdditionalInsuredEndorsementValidatorsMap(nullable ?? false, customMessages);
        // Extract $self from subFieldValidators for item-level validation
        validators.$item = subFieldValidators.$self || [];
        // Store sub-field validators separately
        const { $self, ...fieldValidators } = subFieldValidators;
        validators.$itemFields = fieldValidators as unknown as Record<string, ItemFieldValidator[]>;
    } else {
        validators.$item = [
            ...itemValidators,
            ...createDefaultValidators({ label, type: { $type: itemType } } as FieldSchema, customMessages),
        ];
    }

    return validators;
}
